import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CmmdForgeAutomationPreset,
  CmmdForgeAutomationRule,
  CmmdForgeAutomationRun,
  CmmdStartForgeAutomationRunResult,
} from "@cmmd-forge/contracts";

import { cmmdApiClient } from "~/environments/cmmdApi";
import { sanitizePublicUiError } from "~/productSafeLabels";
import { toastManager } from "./ui/toast";

export interface UseProjectAutomationsStateInput {
  readonly isProjectWorkspace: boolean;
  readonly linkedCmmdProjectId: string | null;
}

export interface ForgeAutomationRunTarget {
  readonly id: number;
  readonly organizationId: number;
}

const STAFFING_AUTHORITY_REQUIRED = "AUTOMATION_STAFFING_AUTHORITY_REQUIRED";

function hasErrorCode(error: unknown, code: string): boolean {
  return typeof error === "object" && error !== null && "code" in error && error.code === code;
}

function isConflict(error: unknown): boolean {
  return typeof error === "object" && error !== null && "status" in error && error.status === 409;
}

export function useProjectAutomationsState(input: UseProjectAutomationsStateInput) {
  const { isProjectWorkspace, linkedCmmdProjectId } = input;
  const [automationPresets, setAutomationPresets] = useState<readonly CmmdForgeAutomationPreset[]>(
    [],
  );
  const [automationRules, setAutomationRules] = useState<readonly CmmdForgeAutomationRule[]>([]);
  const [automationRuns, setAutomationRuns] = useState<readonly CmmdForgeAutomationRun[]>([]);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [automationError, setAutomationError] = useState<string | null>(null);
  const [creatingAutomationPresetKey, setCreatingAutomationPresetKey] = useState<string | null>(
    null,
  );
  const [updatingAutomationRuleId, setUpdatingAutomationRuleId] = useState<number | null>(null);
  const [startingAutomationTaskId, setStartingAutomationTaskId] = useState<number | null>(null);
  const startingAutomationTaskRef = useRef<number | null>(null);

  const refreshProjectAutomations = useCallback(async () => {
    if (!isProjectWorkspace || !linkedCmmdProjectId) {
      setAutomationPresets([]);
      setAutomationRules([]);
      setAutomationRuns([]);
      setAutomationError(null);
      return;
    }

    setAutomationLoading(true);
    setAutomationError(null);
    try {
      const [presets, rules, runs] = await Promise.all([
        cmmdApiClient.listForgeAutomationPresets(),
        cmmdApiClient.listForgeAutomationRules(linkedCmmdProjectId),
        cmmdApiClient.listForgeAutomationRuns(linkedCmmdProjectId),
      ]);
      setAutomationPresets(presets);
      setAutomationRules(rules);
      setAutomationRuns(runs);
    } catch (error: unknown) {
      setAutomationError(sanitizePublicUiError(error, "Unable to load automations."));
    } finally {
      setAutomationLoading(false);
    }
  }, [isProjectWorkspace, linkedCmmdProjectId]);

  useEffect(() => {
    void refreshProjectAutomations();
  }, [refreshProjectAutomations]);

  const handleCreateAutomationPreset = useCallback(
    async (presetKey: string) => {
      if (!linkedCmmdProjectId) return;
      setCreatingAutomationPresetKey(presetKey);
      setAutomationError(null);
      try {
        await cmmdApiClient.createForgeAutomationRule(linkedCmmdProjectId, {
          presetKey,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        });
        await refreshProjectAutomations();
        toastManager.add({
          type: "success",
          title: "Automation created",
          description: "Forge will run it when its trigger or schedule fires.",
        });
      } catch (error: unknown) {
        setAutomationError(sanitizePublicUiError(error, "Unable to create automation."));
      } finally {
        setCreatingAutomationPresetKey(null);
      }
    },
    [linkedCmmdProjectId, refreshProjectAutomations],
  );

  const handleToggleAutomationRule = useCallback(
    async (rule: CmmdForgeAutomationRule) => {
      if (!linkedCmmdProjectId) return;
      setUpdatingAutomationRuleId(rule.id);
      setAutomationError(null);
      try {
        await cmmdApiClient.updateForgeAutomationRule(linkedCmmdProjectId, rule.id, {
          status: rule.status === "active" ? "paused" : "active",
        });
        await refreshProjectAutomations();
      } catch (error: unknown) {
        setAutomationError(sanitizePublicUiError(error, "Unable to update automation."));
      } finally {
        setUpdatingAutomationRuleId(null);
      }
    },
    [linkedCmmdProjectId, refreshProjectAutomations],
  );

  const handleStartTaskRun = useCallback(
    async (taskId: number): Promise<ForgeAutomationRunTarget | null> => {
      if (!linkedCmmdProjectId || startingAutomationTaskRef.current !== null) return null;
      startingAutomationTaskRef.current = taskId;
      setStartingAutomationTaskId(taskId);
      setAutomationError(null);
      try {
        const idempotencyKey = `forge-project-task:${taskId}:${crypto.randomUUID()}`;
        const startRun = () =>
          cmmdApiClient.startForgeAutomationRun(linkedCmmdProjectId, taskId, { idempotencyKey });
        let result: CmmdStartForgeAutomationRunResult;
        try {
          result = await startRun();
        } catch (error: unknown) {
          if (!hasErrorCode(error, STAFFING_AUTHORITY_REQUIRED)) throw error;
          try {
            await cmmdApiClient.activateProjectLeadAutomaticStaffing(linkedCmmdProjectId);
          } catch (activationError: unknown) {
            if (!isConflict(activationError)) throw activationError;
          }
          result = await startRun();
        }
        await refreshProjectAutomations();
        toastManager.add({
          type: "success",
          title: `Run #${result.run.id} started`,
          description: "Forge is planning the work from this CMMD task.",
        });
        return { id: result.run.id, organizationId: result.run.organizationId };
      } catch (error: unknown) {
        setAutomationError(sanitizePublicUiError(error, "Unable to start this Forge run."));
        return null;
      } finally {
        startingAutomationTaskRef.current = null;
        setStartingAutomationTaskId(null);
      }
    },
    [linkedCmmdProjectId, refreshProjectAutomations],
  );

  return {
    automationPresets,
    automationRules,
    automationRuns,
    automationLoading,
    automationError,
    creatingAutomationPresetKey,
    updatingAutomationRuleId,
    startingAutomationTaskId,
    refreshProjectAutomations,
    handleCreateAutomationPreset,
    handleToggleAutomationRule,
    handleStartTaskRun,
  };
}
